import api from 'services/api';
import { array, bool, enforce, nullable, num, object, optional, runSchema, str, unknownValue, withDefault, type FieldParser } from 'validation';
import { nodeInstanceChildEdgeSchema, nodeInstanceRowCoreFields, type NodeInstanceChildEdge, type NodeInstanceRowCore } from './types';

// Loader for `GET /node_instances/:id` (recursive subtree). Wire shapes
// re-derived from nap-backend `getNodeInstances.js`: root SELECT ~141-388,
// children SELECT ~1172-1359 (children are nested under `child_nodes` by the
// route's recursion, not by SQL). See `nodeInstanceWire.ts` for conventions.

/** A flow-graph node position (`JSON_BUILD_OBJECT('x', ..., 'y', ...)`). */
export interface NodeInstancePosition {
  x: number | null;
  y: number | null;
}

const positionSchema: FieldParser<NodeInstancePosition> = object<NodeInstancePosition>({
  x: nullable(num()),
  y: nullable(num()),
});

/**
 * One descendant in a subtree. Children carry `definition_id` (not
 * `node_definition_id`) and flow-layout fields the root omits. `node_definition_id`
 * is kept optional so `getNodeInstanceTree`'s existing fixtures/behaviour are
 * unchanged (real children send `definition_id`).
 */
export interface NodeInstanceSubtreeChild {
  node_instance_id: number;
  parent_node_instance_id: number | null;
  node_id: string | null;
  activated_handles: unknown;
  iteration: number | null;
  definition_id: number;
  node_definition_id?: number;
  definition_version: number | null;
  status: string;
  sub_status: string | null;
  zone_name: string | null;
  has_child_nodes: boolean;
  has_note: boolean;
  has_skippable_wait_events: boolean;
  scheduled_start_datetime: string | null;
  scheduled_start_timezone: string | null;
  start_datetime: string | null;
  end_datetime: string | null;
  runtime_limit_min: number | null;
  runtime_limit_max: number | null;
  duration: number | null;
  created_at: string;
  name: string;
  description: string | null;
  handles: unknown;
  type: string;
  label: string | null;
  spawned_node_instance_id: number | null;
  originating_node_instance_id: number | null;
  submit_and_forget: boolean;
  queue_name: string | null;
  queue_zone_name: string | null;
  worker_name: string | null;
  position: NodeInstancePosition;
  parameters: unknown;
  lock_name: string[] | null;
  file_event_instance_id: number | null;
  file_event_zone_name: string | null;
  event_name: string | null;
  process_event_name: string | null;
  connection_name: string | null;
  scheduled_by: string | null;
  child_nodes: NodeInstanceSubtreeChild[];
}

// Self-referential: `child_nodes` is an array of the same shape. The lambda
// defers the reference so the const is initialised before it's invoked at parse
// time; `array()` drops any structurally broken descendant (pruning its branch
// without failing the whole tree).
const nodeInstanceSubtreeChildSchema: FieldParser<NodeInstanceSubtreeChild> = object<NodeInstanceSubtreeChild>({
  node_instance_id: num(),
  parent_node_instance_id: nullable(num()),
  node_id: nullable(str()),
  activated_handles: unknownValue(),
  iteration: nullable(num()),
  definition_id: num(),
  node_definition_id: optional(num()),
  definition_version: nullable(num()),
  status: str(),
  sub_status: nullable(str()),
  zone_name: nullable(str()),
  has_child_nodes: bool(),
  has_note: bool(),
  has_skippable_wait_events: bool(),
  scheduled_start_datetime: nullable(str()),
  scheduled_start_timezone: nullable(str()),
  start_datetime: nullable(str()),
  end_datetime: nullable(str()),
  runtime_limit_min: nullable(num()),
  runtime_limit_max: nullable(num()),
  duration: nullable(num()),
  created_at: str(),
  name: str(),
  description: nullable(str()),
  handles: unknownValue(),
  type: str(),
  label: nullable(str()),
  spawned_node_instance_id: nullable(num()),
  originating_node_instance_id: nullable(num()),
  submit_and_forget: bool(),
  queue_name: nullable(str()),
  queue_zone_name: nullable(str()),
  worker_name: nullable(str()),
  // Real children always carry position; tolerate its absence so a row that
  // lacks it is defaulted rather than dropped by the enclosing object().
  position: withDefault(positionSchema, { x: null, y: null }),
  parameters: unknownValue(),
  lock_name: nullable(array(str())),
  file_event_instance_id: nullable(num()),
  file_event_zone_name: nullable(str()),
  event_name: nullable(str()),
  process_event_name: nullable(str()),
  connection_name: nullable(str()),
  scheduled_by: nullable(str()),
  child_nodes: array((raw, path, warnings) => nodeInstanceSubtreeChildSchema(raw, path, warnings)),
});

/**
 * The subtree root (`NodeInstanceRowCore` + execution-detail fields). Opaque
 * JSON aggregates are typed `unknown`. Definition snapshots / notes are
 * optional passthroughs — present when the route attaches them, absent
 * otherwise, never dropped.
 */
export interface NodeInstanceSubtreeRoot extends NodeInstanceRowCore {
  current_definition_version: number | null;
  definition_changed_after_execution: boolean;
  executed_definition_config: unknown;
  current_definition_config: unknown;
  status_updates: unknown;
  output: unknown;
  outputdetails: unknown;
  error: unknown;
  activated_handles: unknown;
  parameters: unknown;
  queue_zone_name: string | null;
  has_child_nodes: boolean;
  has_note: boolean;
  submit_and_forget: boolean;
  event_name: string | null;
  executed_definition_snapshot?: unknown;
  current_definition_snapshot?: unknown;
  notes?: unknown;
  child_nodes: NodeInstanceSubtreeChild[];
  child_edges?: NodeInstanceChildEdge[];
}

const nodeInstanceSubtreeRootSchema: FieldParser<NodeInstanceSubtreeRoot> = object<NodeInstanceSubtreeRoot>({
  ...nodeInstanceRowCoreFields,
  current_definition_version: nullable(num()),
  definition_changed_after_execution: bool(),
  executed_definition_config: unknownValue(),
  current_definition_config: unknownValue(),
  status_updates: unknownValue(),
  output: unknownValue(),
  outputdetails: unknownValue(),
  error: unknownValue(),
  activated_handles: unknownValue(),
  parameters: unknownValue(),
  queue_zone_name: nullable(str()),
  has_child_nodes: bool(),
  has_note: bool(),
  submit_and_forget: bool(),
  event_name: nullable(str()),
  executed_definition_snapshot: optional(unknownValue()),
  current_definition_snapshot: optional(unknownValue()),
  notes: optional(unknownValue()),
  child_nodes: array(nodeInstanceSubtreeChildSchema),
  child_edges: optional(array(nodeInstanceChildEdgeSchema)),
});

/**
 * Loads a node instance and its full descendant subtree in one call. Throws if
 * the root is structurally malformed or missing its id; individual broken
 * descendants are pruned.
 */
export const getNodeInstanceSubtree = async (id: number, source = 'getNodeInstanceSubtree'): Promise<NodeInstanceSubtreeRoot> => {
  const res = await api.get(`/node_instances/${id}`);
  const parsed = runSchema(nodeInstanceSubtreeRootSchema, res.data, source);
  if (!parsed.ok) {
    throw new Error(`${source}: response failed validation`);
  }
  return enforce(parsed.value, parsed.warnings, ['node_instance_id']);
};
